import { useState, type ReactNode } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BsBoxSeam, BsPalette, BsSliders } from 'react-icons/bs';
import Skeleton from 'react-loading-skeleton';
import { useStockList, useTrims, useVehicle } from '../api';
import { useConfiguratorState } from '../store';
import { useShowIncompatible } from '../showIncompatibleStore';
import { ApiValidationDialog } from '../ApiValidationDialog';
import type { CustomizerTab } from '../domain';
import { ConfiguratorSearchInput, QueryStateBoundary } from '../common';
import { useConfiguratorTranslations } from '../translations';
import { TrimList } from './TrimSelector';
import { OptionGroupList } from './CustomizerOptions';
import { VehicleSpecPanel } from './VehicleSpecPanel';
import { StockList } from './StockSelector';
import { useStockRestore } from '../savedConfigurations/stockRestore';
import { VehicleModeChips } from './VehicleModeChips';
import styles from './Customizer.module.scss';

function CustomizerBreadcrumb() {
	const subsection = useConfiguratorState((s) => s.subsection);
	const backToEngine = useConfiguratorState((s) => s.backToEngine);
	const { t } = useTranslation();

	const root = t('car_configurator_customizer_breadcrumb_root', {
		defaultValue: 'PERSONALIZZAZIONE',
	});
	const section =
		subsection === 'engine'
			? t('car_configurator_customizer_breadcrumb_engine', {
					defaultValue: 'MOTORE & ALLESTIMENTO',
			  })
			: t('car_configurator_customizer_breadcrumb_exterior_interior', {
					defaultValue: 'ESTERNI & INTERNI',
			  });

	return (
		<div className={styles.breadcrumb}>
			{subsection === 'exterior-interior' ? (
				<button className={styles.breadcrumbClickable} onClick={backToEngine}>
					{root}
				</button>
			) : (
				<span>{root}</span>
			)}{' '}
			<span className={styles.breadcrumbSep}>›</span>{' '}
			<span className={styles.breadcrumbActive}>{section}</span>
		</div>
	);
}

function ShowIncompatibleToggle() {
	const show = useShowIncompatible((s) => s.show);
	const toggle = useShowIncompatible((s) => s.toggle);
	const { t } = useTranslation();

	return (
		<div className={styles.showIncompatible}>
			<span className={styles.showIncompatibleLabel}>
				{t('car_configurator_customizer_show_incompatible', {
					defaultValue: 'Mostra non compatibili',
				})}
			</span>
			<Form.Check
				type="switch"
				className="switch"
				id="mn-show-incompatible"
				checked={show}
				onChange={toggle}
			/>
		</div>
	);
}

function CustomizerTabBar() {
	const subsection = useConfiguratorState((s) => s.subsection);
	const tab = useConfiguratorState((s) => s.tab);
	const setTab = useConfiguratorState((s) => s.setTab);
	const { t } = useTranslation();

	if (subsection !== 'exterior-interior') return null;

	const TABS: Array<{ key: CustomizerTab; label: string; icon: ReactNode }> = [
		{
			key: 'colors',
			label: t('car_configurator_customizer_tab_colors', {
				defaultValue: 'Colori',
			}),
			icon: <BsPalette />,
		},
		{
			key: 'options',
			label: t('car_configurator_customizer_tab_options', {
				defaultValue: 'Optional',
			}),
			icon: <BsSliders />,
		},
		{
			key: 'packs',
			label: t('car_configurator_customizer_tab_packs', {
				defaultValue: 'Pacchetti',
			}),
			icon: <BsBoxSeam />,
		},
	];

	return (
		<div className={styles.subTabBar}>
			{TABS.map((tabItem) => (
				<button
					key={tabItem.key}
					className={`${styles.subTabBtn} ${
						tab === tabItem.key ? styles.subTabActive : ''
					}`}
					onClick={() => setTab(tabItem.key)}
				>
					<span className={styles.tabIcon}>{tabItem.icon}</span>
					{tabItem.label}
				</button>
			))}
			<ShowIncompatibleToggle />
		</div>
	);
}

function EngineTab() {
	const makeCode = useConfiguratorState((s) => s.makeCode);
	const modelCode = useConfiguratorState((s) => s.modelCode);
	const vehicleMode = useConfiguratorState((s) => s.vehicleMode);
	const trimsQuery = useTrims(makeCode, modelCode);
	const stockQuery = useStockList(makeCode, modelCode);
	const tr = useConfiguratorTranslations();
	const [search, setSearch] = useState('');

	// Qui e non in StockList: la modalità va impostata prima che la lista esista.
	useStockRestore(stockQuery.data?.vehicles);

	const stockTotal = stockQuery.data?.total ?? 0;

	return (
		<section>
			<ConfiguratorSearchInput
				placeholder={
					vehicleMode === 'stock'
						? tr.stockSearchPlaceholder
						: tr.trimSearchPlaceholder
				}
				value={search}
				onChange={setSearch}
				className="mb-3"
			/>

			<VehicleModeChips
				stockTotal={stockTotal}
				disabled={!stockQuery.isSuccess || stockTotal === 0}
			/>

			{vehicleMode === 'stock' ? (
				<QueryStateBoundary
					query={stockQuery}
					skeleton={<Skeleton count={3} height={72} />}
				>
					{(data) => (
						<StockList
							vehicles={data.vehicles}
							total={data.total}
							search={search}
						/>
					)}
				</QueryStateBoundary>
			) : (
				<QueryStateBoundary
					query={trimsQuery}
					skeleton={<Skeleton count={3} height={72} />}
				>
					{(trims) => <TrimList trims={trims} search={search} />}
				</QueryStateBoundary>
			)}
		</section>
	);
}

export function Customizer() {
	const subsection = useConfiguratorState((s) => s.subsection);
	const tab = useConfiguratorState((s) => s.tab);
	const vehicle = useConfiguratorState((s) => s.vehicle);
	const vehicleMode = useConfiguratorState((s) => s.vehicleMode);
	const stockVehicle = useConfiguratorState((s) => s.stockVehicle);
	const makeCode = useConfiguratorState((s) => s.makeCode);
	const modelCode = useConfiguratorState((s) => s.modelCode);
	const makeLabel = useConfiguratorState((s) => s.makeLabel);
	const modelLabel = useConfiguratorState((s) => s.modelLabel);
	const vehicleQuery = useVehicle(vehicle);
	const trimsQuery = useTrims(makeCode, modelCode);
	const [searchQuery, setSearchQuery] = useState('');
	const { t } = useTranslation();
	const tr = useConfiguratorTranslations();

	const heading =
		makeLabel && modelLabel
			? t('car_configurator_customizer_heading_model', {
					defaultValue: 'Personalizza la tua {{make}} {{model}}',
					make: makeLabel,
					model: modelLabel,
			  })
			: t('car_configurator_customizer_heading_generic', {
					defaultValue: 'Personalizza la tua auto',
			  });

	const emptyMessage = searchQuery
		? t('car_configurator_customizer_no_optional_found', {
				defaultValue: 'Nessun optional trovato',
		  })
		: t('car_configurator_customizer_no_optional_available', {
				defaultValue: 'Nessun optional disponibile',
		  });

	const noVehicleHint = t('car_configurator_customizer_no_vehicle_hint', {
		defaultValue: 'Seleziona un motore e allestimento per vedere il riepilogo',
	});

	return (
		<section>
			<Row className="g-4 align-items-stretch">
				<Col lg={8}>
					<CustomizerBreadcrumb />
					<h1 className={styles.pageHeading}>{heading}</h1>

					<CustomizerTabBar />

					{subsection === 'engine' && <EngineTab />}

					{subsection === 'exterior-interior' && !vehicle && (
						<div className={styles.noVehicleHint}>{noVehicleHint}</div>
					)}

					{subsection === 'exterior-interior' && vehicle && (
						<>
							<ConfiguratorSearchInput
								placeholder={t(
									'car_configurator_customizer_search_placeholder',
									{
										defaultValue:
											'Cerca optional per nome o codice (es. Vernice, LED, W3P...)',
									}
								)}
								value={searchQuery}
								onChange={setSearchQuery}
								className="mb-3"
							/>
							<QueryStateBoundary
								query={vehicleQuery}
								skeleton={<Skeleton count={5} height={64} />}
							>
								{(data) => {
									const subset =
										tab === 'colors'
											? data.catalog.byBucket.colors
											: tab === 'options'
											? data.catalog.byBucket.options
											: data.catalog.byBucket.packs;
									return (
										<OptionGroupList
											tab={tab}
											options={subset}
											searchQuery={searchQuery}
											emptyMessage={emptyMessage}
										/>
									);
								}}
							</QueryStateBoundary>
						</>
					)}
				</Col>

				<Col lg={4} className="d-flex flex-column">
					{/* in pronta consegna la scheda descrive lo stock e non attende un
					    allestimento, che lì è dormiente e può non esserci affatto; senza
					    riga scelta non c'è nulla da quotare, e un prezzo a zero in un
					    listino si legge come un'offerta */}
					{vehicleMode === 'stock' ? (
						stockVehicle ? (
							<VehicleSpecPanel />
						) : (
							<div className={styles.noVehicleCard}>
								<div className={styles.noVehicleHint}>
									{tr.stockNoSelectionHint}
								</div>
							</div>
						)
					) : vehicle ? (
						<VehicleSpecPanel />
					) : trimsQuery.isLoading ? (
						<div className={styles.noVehicleCard}>
							<Skeleton count={4} height={20} />
						</div>
					) : (
						<div className={styles.noVehicleCard}>
							<div className={styles.noVehicleHint}>{noVehicleHint}</div>
						</div>
					)}
				</Col>
			</Row>
			<ApiValidationDialog />
		</section>
	);
}
